// src/components/SmartOrderPlanner.js

import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';

export default function SmartOrderPlanner() {
  const [recipes, setRecipes] = useState([]);
  const [warehouseMap, setWarehouseMap] = useState({});
  const [shopMap, setShopMap] = useState({});
  const [pansByRecipe, setPansByRecipe] = useState({});
  const [rows, setRows] = useState([]);

  const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

  useEffect(() => {
    const fetchData = async () => {
      const recipesSnap = await getDocs(collection(db, 'recipes'));
      const warehouseSnap = await getDocs(collection(db, 'stock_store'));
      const shopSnap = await getDocs(collection(db, 'stock_shop'));

      setRecipes(
        recipesSnap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
      );

      const warehouse = {};
      warehouseSnap.forEach((docSnap) => {
        warehouse[docSnap.id] = docSnap.data();
      });

      const shop = {};
      shopSnap.forEach((docSnap) => {
        shop[docSnap.id] = docSnap.data();
      });

      setWarehouseMap(warehouse);
      setShopMap(shop);
    };

    fetchData();
  }, []);

  const selectedRecipes = useMemo(() => {
    return recipes
      .map((recipe) => ({
        ...recipe,
        plannedPans: Number(pansByRecipe[recipe.id] || 0),
      }))
      .filter((recipe) => recipe.plannedPans > 0);
  }, [recipes, pansByRecipe]);

  const shouldIgnoreIngredient = (ingredient) => {
    const product = String(ingredient?.product || '').trim().toLowerCase();

    return (
      !ingredient?.code ||
      product.includes('pulp') ||
      product.startsWith('water')
    );
  };

  const generatePlan = () => {
    const ingredientMap = {};

    selectedRecipes.forEach((recipe) => {
      const pans = Number(recipe.plannedPans || 0);

      (recipe.ingredients || [])
        .filter((ingredient) => !shouldIgnoreIngredient(ingredient))
        .forEach((ingredient) => {
          const code = String(ingredient.code || '').trim();
          const product = ingredient.product || code;
          const requiredKg = (Number(ingredient.amount_g || 0) * pans) / 1000;

          if (!ingredientMap[code]) {
            ingredientMap[code] = {
              code,
              ingredient: product,
              requiredKg: 0,
              usedIn: [],
            };
          }

          ingredientMap[code].requiredKg += requiredKg;
          ingredientMap[code].usedIn.push(
            `${recipe.name}: ${pans} pan${pans !== 1 ? 's' : ''}`
          );
        });
    });

    const result = Object.values(ingredientMap)
      .map((item) => {
        const warehouseStock = warehouseMap[item.code] || {};
        const shopStock = shopMap[item.code] || {};

        const boxWeight =
          Number(warehouseStock.boxWeight || 0) ||
          Number(shopStock.boxWeight || 0);

        const warehouseKg = Number(warehouseStock.totalKg || 0);
        const shopKg = Number(shopStock.totalKg || 0);
        const availableKg = warehouseKg + shopKg;

        const requiredBoxes =
          boxWeight > 0 ? item.requiredKg / boxWeight : 0;

        const warehouseBoxes =
          boxWeight > 0 ? warehouseKg / boxWeight : 0;

        const shopBoxes =
          boxWeight > 0 ? shopKg / boxWeight : 0;

        const availableBoxes =
          boxWeight > 0 ? availableKg / boxWeight : 0;

        const shortageKg = Math.max(item.requiredKg - availableKg, 0);

        const shortageBoxes =
          boxWeight > 0 ? shortageKg / boxWeight : 0;

        const orderBoxes =
          boxWeight > 0 ? Math.ceil(shortageBoxes) : 0;

        const boughtKg = orderBoxes * boxWeight;
        const extraKgAfterOrder = boughtKg - shortageKg;

        return {
          ...item,
          requiredKg: round2(item.requiredKg),
          requiredBoxes: round2(requiredBoxes),

          boxWeight: round2(boxWeight),

          warehouseKg: round2(warehouseKg),
          warehouseBoxes: round2(warehouseBoxes),

          shopKg: round2(shopKg),
          shopBoxes: round2(shopBoxes),

          availableKg: round2(availableKg),
          availableBoxes: round2(availableBoxes),

          shortageKg: round2(shortageKg),
          shortageBoxes: round2(shortageBoxes),

          orderBoxes,
          boughtKg: round2(boughtKg),
          extraKgAfterOrder: round2(extraKgAfterOrder),
        };
      })
      .sort((a, b) => b.orderBoxes - a.orderBoxes || b.requiredBoxes - a.requiredBoxes);

    setRows(result);
  };

  const clearPlan = () => {
    setPansByRecipe({});
    setRows([]);
  };

  const exportCSV = () => {
    if (!rows.length) return;

    const headers = [
      'Code',
      'Ingredient',
      'Required Boxes',
      'Shop Boxes',
      'Warehouse Boxes',
      'Total Available Boxes',
      'Shortage Boxes',
      'Order Boxes',
      'Required Kg',
      'Available Kg',
      'Shortage Kg',
      'Box Weight Kg',
      'Used In',
    ];

    const csvRows = rows.map((r) => [
      r.code,
      r.ingredient,
      r.requiredBoxes.toFixed(2),
      r.shopBoxes.toFixed(2),
      r.warehouseBoxes.toFixed(2),
      r.availableBoxes.toFixed(2),
      r.shortageBoxes.toFixed(2),
      r.orderBoxes,
      r.requiredKg.toFixed(2),
      r.availableKg.toFixed(2),
      r.shortageKg.toFixed(2),
      r.boxWeight.toFixed(2),
      `"${r.usedIn.join(' | ')}"`,
    ]);

    const csv = [
      headers.join(','),
      ...csvRows.map((row) => row.join(',')),
    ].join('\n');

    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = 'smart_order_planner.csv';
    link.click();

    URL.revokeObjectURL(url);
  };

  return (
    <div className="smart-order-planner">
      <h2>🧠 Smart Order Planner</h2>

      <p style={{ marginBottom: 20 }}>
        Enter planned pans for all flavours. This combines shared ingredients, checks shop + warehouse stock, then tells you what boxes to buy.
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 12,
          marginBottom: 20,
        }}
      >
        {recipes.map((recipe) => (
          <div
            key={recipe.id}
            style={{
              border: '1px solid #ddd',
              borderRadius: 8,
              padding: 12,
              background: '#fff',
            }}
          >
            <label>
              <strong>{recipe.name}</strong>
            </label>

            <input
              type="number"
              min="0"
              step="1"
              placeholder="Pans"
              value={pansByRecipe[recipe.id] || ''}
              onChange={(e) =>
                setPansByRecipe((prev) => ({
                  ...prev,
                  [recipe.id]: e.target.value,
                }))
              }
              style={{
                width: '100%',
                marginTop: 8,
                padding: 8,
              }}
            />
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
        <button
          className="btn btn-primary"
          onClick={generatePlan}
          disabled={!selectedRecipes.length}
        >
          Generate Smart Order
        </button>

        <button className="btn" onClick={clearPlan}>
          Clear
        </button>

        {rows.length > 0 && (
          <button className="btn" onClick={exportCSV}>
            📥 Export CSV
          </button>
        )}
      </div>

      {selectedRecipes.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <strong>Selected:</strong>{' '}
          {selectedRecipes
            .map((r) => `${r.name} (${r.plannedPans} pan${r.plannedPans !== 1 ? 's' : ''})`)
            .join(', ')}
        </div>
      )}

      {rows.length > 0 && (
        <table
          border="1"
          cellPadding="8"
          style={{
            width: '100%',
            textAlign: 'center',
            borderCollapse: 'collapse',
          }}
        >
          <thead>
            <tr>
              <th>Code</th>
              <th>Ingredient</th>
              <th>Required Boxes</th>
              <th>Shop Boxes</th>
              <th>Warehouse Boxes</th>
              <th>Total Available</th>
              <th>Shortage</th>
              <th>Order Boxes</th>
              <th>Required kg</th>
              <th>Box kg</th>
              <th>Used In</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((r) => (
              <tr
                key={r.code}
                style={{
                  background: r.orderBoxes > 0 ? '#fff4f4' : '#f4fff4',
                }}
              >
                <td>{r.code}</td>
                <td>{r.ingredient}</td>
                <td>{r.requiredBoxes.toFixed(2)}</td>
                <td>{r.shopBoxes.toFixed(2)}</td>
                <td>{r.warehouseBoxes.toFixed(2)}</td>
                <td>{r.availableBoxes.toFixed(2)}</td>
                <td>{r.shortageBoxes.toFixed(2)}</td>
                <td>
                  <strong>{r.orderBoxes}</strong>
                </td>
                <td>{r.requiredKg.toFixed(2)}</td>
                <td>{r.boxWeight.toFixed(2)}</td>
                <td style={{ textAlign: 'left' }}>
                  {r.usedIn.join(', ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}